import dayjs, { Dayjs } from "dayjs";
import { useIntl } from "react-intl";
import { StyledDateRangeFilter } from "./index.styled";
import { DatePicker, Typography } from "antd";
import type { TimeRangePickerProps } from "antd";
import useScreen from "@lib/hooks/useScreen";
import { CSSProperties, memo } from "react";

const { RangePicker } = DatePicker;

type Props = {
  value: [string, string];
  onChange?: (value: [Dayjs, Dayjs]) => void;
  rangePresets?: TimeRangePickerProps["presets"];
  dontShowPresets?: boolean;
  onlyShowDate?: boolean;
  showDateStyle?: CSSProperties;
};

function DateRangeFilter(props: Props) {
  const { value, onChange, dontShowPresets, onlyShowDate, showDateStyle } =
    props;
  const { messages } = useIntl();
  const { isMobile } = useScreen();

  const rangePresets: TimeRangePickerProps["presets"] = props?.rangePresets || [
    {
      label: messages["common.today"] as string,
      value: [dayjs(), dayjs()],
    },
    {
      label: messages["common.thisWeek"] as string,
      value: [dayjs().startOf("week"), dayjs().endOf("week")],
    },
    {
      label: messages["common.thisMonth"] as string,
      value: [dayjs().startOf("month"), dayjs().endOf("month")],
    },
    {
      label: messages["common.thisYear"] as string,
      value: [dayjs().startOf("year"), dayjs().endOf("year")],
    },
    {
      label: messages["common.last7Days"] as string,
      value: [dayjs().add(-7, "d"), dayjs()],
    },
    {
      label: messages["common.last14Days"] as string,
      value: [dayjs().add(-14, "d"), dayjs()],
    },
    {
      label: messages["common.last30Days"] as string,
      value: [dayjs().add(-30, "d"), dayjs()],
    },
    {
      label: messages["common.last90Days"] as string,
      value: [dayjs().add(-90, "d"), dayjs()],
    },
  ];

  if (onlyShowDate) {
    return (
      <Typography style={{ ...showDateStyle }}>
        {dayjs(value[0]).format("DD MMM")} - {dayjs(value[1]).format("DD MMM")}
      </Typography>
    );
  }

  return (
    <StyledDateRangeFilter mobile={isMobile}>
      <RangePicker
        value={[dayjs(value[0]), dayjs(value[1])]}
        presets={dontShowPresets ? undefined : rangePresets}
        onChange={(dates) => {
          if (dates) {
            onChange([dayjs(dates[0]), dayjs(dates[1])]);
          }
        }}
        style={{ width: "100%" }}
      />
    </StyledDateRangeFilter>
  );
}

export default memo(DateRangeFilter, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value;
});
